import { useEffect, useState } from "react";

import AccountService from "../../services/AccountService";
import { CurrentUser } from "../../types/User";
import { useAlertContext } from "../alert/AlertContext";
import { AuthContext } from "./AuthContext";

export interface AuthProps {
  children: React.ReactNode;
}

const USER_DETAILS_KEY = "userDetails";

const getUserDetails = (): CurrentUser | undefined => {
  const user = localStorage.getItem(USER_DETAILS_KEY);
  if (user != null) {
    return JSON.parse(user) as CurrentUser;
  } else {
    return undefined;
  }
};

const AuthProvider = ({ children }: AuthProps) => {
  const [user, setUser] = useState<CurrentUser | undefined>(getUserDetails());
  const alertContext = useAlertContext();
  const accountService = new AccountService(alertContext.showAlert);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshGraceMinutes = 10;
  const tokenHeartbeatInterval = 60000;

  const checkTokenExpiry = () => {
    if (user && !isRefreshing) {
      if(user.tokenExpiry === undefined || user.refreshToken === undefined) {
        logout();
        return;
      }

      const expiry = new Date(user.tokenExpiry);
      if (expiry < new Date()) {
        refreshToken(user, true);
      } else {
        const diff = expiry.getTime() - new Date().getTime();
        const remainingMinutes = Math.floor(diff / 60000);
        if (remainingMinutes <= refreshGraceMinutes) {
          refreshToken(user, false);
        }
      }
    }
  };

  const refreshToken = (user: CurrentUser, requirePageRefreshOnSuccess: boolean) => {
    setIsRefreshing(true);

    let token = user.token;
    let refreshToken = user.refreshToken;
    let clientId = process.env.REACT_APP_CLIENT_ID;

    if(clientId === undefined) {
      return;
    }

    accountService
      .RefreshToken(token, refreshToken, clientId)
      .then((res) => {
        setUser({
          ...user,
          token: res.token,
          tokenExpiry: res.tokenExpiry,
          refreshToken: res.refreshToken,
        });

        if(requirePageRefreshOnSuccess) {
          window.location.reload();
        }
      })
      .catch((error) => {
        logout();
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  useEffect(() => {
    checkTokenExpiry();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (!isRefreshing) {
      interval = setInterval(checkTokenExpiry, tokenHeartbeatInterval);
    }
    return () => {
      clearInterval(interval);
    };
  }, [user, isRefreshing]);

  useEffect(() => {
    if (user) {
      localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_DETAILS_KEY);
    }
  }, [user]);

  const login = (user: CurrentUser) => {
    setUser(user);
  };

  const logout = () => {
    setUser(undefined);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
export { getUserDetails };

